import { useMutation, useQueryClient } from '@tanstack/react-query'
import { notificationApi } from '../api/client'
import { notificationKeys } from './keys'
import type { NotificationsInboxResponse } from '../api/types'

export function useMarkRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => notificationApi.markRead(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: [...notificationKeys.all, 'inbox'] })
      const snapshots = qc.getQueriesData<NotificationsInboxResponse>({ queryKey: [...notificationKeys.all, 'inbox'] })
      const now = new Date().toISOString()
      qc.setQueriesData<NotificationsInboxResponse>(
        { queryKey: [...notificationKeys.all, 'inbox'] },
        (old) => {
          if (!old) return old
          const wasUnread = old.items.items.some((n) => n.id === id && n.readAt === null)
          return {
            items: {
              ...old.items,
              items: old.items.items.map((n) => (n.id === id ? { ...n, readAt: n.readAt ?? now } : n)),
            },
            unreadCount: wasUnread ? Math.max(0, old.unreadCount - 1) : old.unreadCount,
          }
        },
      )
      return { snapshots }
    },
    onError: (_err, _id, context) => {
      if (context?.snapshots) {
        for (const [key, data] of context.snapshots) {
          qc.setQueryData(key, data)
        }
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

export function useDismiss() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => notificationApi.dismiss(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: [...notificationKeys.all, 'inbox'] })
      const snapshots = qc.getQueriesData<NotificationsInboxResponse>({ queryKey: [...notificationKeys.all, 'inbox'] })
      qc.setQueriesData<NotificationsInboxResponse>(
        { queryKey: [...notificationKeys.all, 'inbox'] },
        (old) => {
          if (!old) return old
          const wasUnread = old.items.items.some((n) => n.id === id && n.readAt === null)
          return {
            items: {
              ...old.items,
              items: old.items.items.filter((n) => n.id !== id),
            },
            unreadCount: wasUnread ? Math.max(0, old.unreadCount - 1) : old.unreadCount,
          }
        },
      )
      return { snapshots }
    },
    onError: (_err, _id, context) => {
      if (context?.snapshots) {
        for (const [key, data] of context.snapshots) {
          qc.setQueryData(key, data)
        }
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

export function useAcknowledge() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (alertId: number) => notificationApi.acknowledge(alertId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

export function useUpsertOrgDefault() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: notificationApi.upsertOrgDefault,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.orgDefaults() })
    },
  })
}
